import { supabase } from '@/lib/supabase';

/* ------------------------------------------------------------------ */
/* Driver Payment Builder — configurable payslip / summary layout      */
/*                                                                     */
/* Instead of hard-coding which turnover columns appear on the weekly  */
/* driver summary + payslip, this config is stored in the `settings`   */
/* table (category 'PayslipBuilder', name 'payslip_builder_config') as */
/* a single JSON blob. The Driver Summary modal, the PDF and the email */
/* payslip all read from here, so admins can add/remove/reorder columns*/
/* and brand the payslip without touching code.                        */
/* ------------------------------------------------------------------ */

/** A single turnover column shown on the summary + payslip. */
export interface PayslipColumn {
  /** Stable source key on a weekly-payout job row (e.g. 'ecard_amt'). */
  key: string;
  /** Display label shown to drivers (e.g. 'eCard'). */
  label: string;
  /** Payment-type name used to resolve the driver-payable % (commission). */
  paymentType: string | null;
  /** Whether this column is shown on the summary/payslip. */
  visible: boolean;
}

export interface PayslipBranding {
  /** Company / trading name printed at the top of the payslip. */
  companyName: string;
  /** Optional tagline / sub-heading under the company name. */
  tagline: string;
  /** Optional logo image URL. */
  logoUrl: string;
  /** Accent colour for headers / totals (hex). */
  accentColor: string;
  /** Footer note printed at the bottom (e.g. payment terms / contact). */
  footerNote: string;
  /** Show the day-by-day breakdown table on the payslip. */
  showDailyBreakdown: boolean;
}
/**
 * A layout block that can be dragged onto the payslip in the Driver Payment
 * Builder (logo, heading, text line, tagline, footer, spacer, the daily
 * amounts table). These are rendered, in this exact order, by both the
 * builder's mock preview AND the real PDF/email payslip (see payslip.ts) --
 * so what you build here is genuinely what prints.
 */
export interface PayslipElement {
  /** Stable id for drag/drop + react keys. */
  id: string;
  /** Block type. 'daily_table' and 'summary' have no value/align of their
   *  own -- 'daily_table' renders the date/jobs/turnover-columns table using
   *  the columns configured below, 'summary' renders the Gross Turnover ->
   *  Total Payment card. Both are single-instance (only one of each makes
   *  sense on a payslip). */
  type: 'logo' | 'heading' | 'text' | 'tagline' | 'footer' | 'spacer' | 'daily_table' | 'summary';
  /** Text value (or image URL for logo). */
  value: string;
  /** Horizontal alignment. */
  align: 'left' | 'center' | 'right';
}

export interface PayslipConfig {
  columns: PayslipColumn[];
  branding: PayslipBranding;
  /** Optional free-form header/footer blocks (logo, text, etc.). */
  elements?: PayslipElement[];
}


/* ---- Seed: mirrors the existing hard-coded layout exactly ---- */
export const DEFAULT_PAYSLIP_CONFIG: PayslipConfig = {
  columns: [
    { key: 'cash_amt', label: 'Cash', paymentType: 'Cash', visible: true },
    { key: 'eftpos_amt', label: 'EFTPOS', paymentType: 'EFTPOS', visible: true },
    { key: 'totalmobility_amt', label: 'Total Mobility', paymentType: 'TM Subsidy', visible: true },
    { key: 'tm_hoist_amt', label: 'TM Hoist', paymentType: 'TM Hoist', visible: true },
    { key: 'discount', label: 'Discount', paymentType: 'Discount', visible: true },
    { key: 'account_amt', label: 'Account', paymentType: 'Account', visible: true },
    { key: 'other_amt', label: 'Other', paymentType: null, visible: true },
    { key: 'ecard_amt', label: 'eCard', paymentType: 'e-card', visible: true },
  ],
  branding: {
    companyName: 'Weekly Payment Summary',
    tagline: '',
    logoUrl: '',
    accentColor: '#16a34a',
    footerNote: '',
    showDailyBreakdown: true,
  },
  elements: [
    { id: 'logo-default', type: 'logo', value: '', align: 'center' },
    { id: 'heading-default', type: 'heading', value: 'Weekly Payment Summary', align: 'center' },
    { id: 'tagline-default', type: 'tagline', value: '', align: 'center' },
    { id: 'daily-default', type: 'daily_table', value: '', align: 'center' },
    { id: 'summary-default', type: 'summary', value: '', align: 'center' },
    { id: 'footer-default', type: 'footer', value: '', align: 'center' },
  ],
};

/* The full set of source columns that exist on a weekly-payout job row.
   Used by the builder to let admins add a column that isn't shown yet. */
export const AVAILABLE_SOURCE_COLUMNS: Array<{ key: string; label: string }> = [
  { key: 'cash_amt', label: 'Cash' },
  { key: 'eftpos_amt', label: 'EFTPOS' },
  { key: 'totalmobility_amt', label: 'Total Mobility' },
  { key: 'tm_hoist_amt', label: 'TM Hoist' },
  { key: 'account_amt', label: 'Account' },
  { key: 'ecard_amt', label: 'eCard' },
  { key: 'discount', label: 'Discount' },
  { key: 'other_amt', label: 'Other' },
];

/** Tokens that can be typed into a Text/Heading/Tagline/Footer block's value
 *  and get substituted with the real driver/week values when the payslip
 *  actually renders (both the mock preview and the real PDF/email). */
export const PAYSLIP_PLACEHOLDERS: { token: string; label: string }[] = [
  { token: '{driver_name}', label: 'Driver Name' },
  { token: '{week_start}', label: 'Week Start' },
  { token: '{week_end}', label: 'Week Ending' },
];

export function applyPayslipPlaceholders(
  text: string,
  values: { driverName: string; weekStart: string; weekEnd: string },
): string {
  return text
    .replace(/\{driver_name\}/gi, values.driverName)
    .replace(/\{week_start\}/gi, values.weekStart)
    .replace(/\{week_end\}/gi, values.weekEnd);
}

const SETTING_NAME = 'payslip_builder_config';

/** Merge a stored partial config onto the defaults so missing keys still work. */
const mergeConfig = (raw: Partial<PayslipConfig> | null | undefined): PayslipConfig => {
  if (!raw || typeof raw !== 'object') return { ...DEFAULT_PAYSLIP_CONFIG };
  const columns = Array.isArray(raw.columns) && raw.columns.length
    ? raw.columns.map((c) => ({
        key: String(c.key),
        label: String(c.label ?? c.key),
        paymentType: c.paymentType ?? null,
        visible: c.visible !== false,
      }))
    : DEFAULT_PAYSLIP_CONFIG.columns.map((c) => ({ ...c }));
  const elements = Array.isArray(raw.elements)
    ? raw.elements
        .filter((e) => e && typeof e === 'object')
        .map((e, i) => ({
          id: String(e.id ?? `el-${i}-${Math.random().toString(36).slice(2, 8)}`),
          type: (e.type ?? 'text') as PayslipElement['type'],
          value: String(e.value ?? ''),
          align: (e.align ?? 'center') as PayslipElement['align'],
        }))
    : undefined;
  return {
    columns,
    branding: { ...DEFAULT_PAYSLIP_CONFIG.branding, ...(raw.branding || {}) },
    ...(elements ? { elements } : {}),
  };
};


export const loadPayslipConfig = async (
  tenantId?: string | null,
): Promise<PayslipConfig> => {
  let query = supabase
    .from('settings')
    .select('value, tenant_id')
    .eq('name', SETTING_NAME);

  if (tenantId) query = query.eq('tenant_id', tenantId);

  const { data, error } = await query.maybeSingle();
  if (error || !data?.value) {
    return { ...DEFAULT_PAYSLIP_CONFIG };
  }
  try {
    return mergeConfig(JSON.parse(data.value));
  } catch {
    return { ...DEFAULT_PAYSLIP_CONFIG };
  }
};

export const savePayslipConfig = async (
  config: PayslipConfig,
  tenantId: string | null,
): Promise<void> => {
  const row = {
    name: SETTING_NAME,
    value: JSON.stringify(config),
    category: 'PayslipBuilder',
    label: 'Driver Payment Builder layout',
    description: 'Configurable payment summary columns + branding for the driver weekly summary.',
    type: 'json',
    sort_order: 10,
    is_active: true,
    tenant_id: tenantId,
  };

  // Remove any existing row(s) for this tenant, then insert (avoids needing a
  // unique constraint on name for upsert).
  let del = supabase.from('settings').delete().eq('name', SETTING_NAME);
  if (tenantId) del = del.eq('tenant_id', tenantId);
  await del;

  const { error } = await supabase.from('settings').insert([row]);
  if (error) throw error;
};
